import { useEffect, useState } from 'react'
import { getBreakpoint } from './breakpoints.js'

/**
 * Single source of truth for the current viewport breakpoint.
 * Ladder + rationale: ./breakpoints.js
 *
 * Returns one of: 'phone' | 'tablet' | 'desktop'.
 *
 * React's useState short-circuits identical-value updates, so calling setBp on
 * every resize event is cheap — only band changes produce a re-render. No need
 * to throttle / rAF the listener.
 *
 * Prefer this over a CSS `@media` rule when the change is structural (which
 * component renders) rather than cosmetic (how it looks). Two reasons, both
 * learned here: jsdom has no layout engine, so `@media` behaviour is untestable
 * in the unit suite; and the repo's own CSS-media attempt at responsive tables
 * had to be `!important`-overridden by the page that cared most
 * (warehouse/PutawayResults.jsx:88).
 */
export default function useBreakpoint() {
  const [bp, setBp] = useState(() =>
    typeof window === 'undefined' ? 'desktop' : getBreakpoint(window.innerWidth)
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    function onResize() {
      setBp(getBreakpoint(window.innerWidth))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return bp
}
